import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { useState, useCallback } from "react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { CountryCode } from "libphonenumber-js";
import { useTranslation } from "react-i18next";
import { OnboardingLayout } from "@/components/onboarding-layout";
import { WaitlistFormCard } from "@/components/onboarding/WaitlistFormCard";
import type { WaitlistFormData } from "@/components/onboarding/schema";
import { logError, logInfo } from "@/lib/structured-log";
import { submitWaitlistServer } from "@/server/onboarding";
import { extractApiErrorMessage } from "@/lib/api/error";

export const Route = createFileRoute("/onboarding/waitlist/")({
  // No beforeLoad needed - parent route (/onboarding) already handles:
  // 1. Authentication check
  // 2. Loading onboarding status into context
  // 3. State machine redirects in loader
  component: WaitlistPage,
});

function WaitlistPage() {
  const { t } = useTranslation(["pages", "common"]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const router = useRouter();

  const handleWaitlistSubmit = useCallback(
    async (data: WaitlistFormData) => {
      setError("");
      setIsLoading(true);
      logInfo("onboarding.waitlist.requested", {
        has_name: !!data.name,
        has_country_code: !!data.countryCode,
        has_phone_number: !!data.phoneNumber,
        has_source: !!data.source,
        has_website: !!data.website,
        has_comments: !!data.comments,
      });

      try {
        const parsedNumber = parsePhoneNumberFromString(
          data.phoneNumber,
          data.countryCode.toUpperCase() as CountryCode,
        );

        if (!parsedNumber?.isValid()) {
          throw new Error("Invalid phone number");
        }

        // Convert camelCase to snake_case for API
        const phoneNumber = parsedNumber.number;
        const status = await submitWaitlistServer({
          data: {
            name: data.name,
            phone_number: phoneNumber,
            website: data.website || undefined,
            source: data.source,
            comments: data.comments || undefined,
          },
        });
        logInfo("onboarding.waitlist.succeeded", {
          onboarding_state: status.onboarding_state,
          waitlist_status: status.waitlist?.status ?? null,
        });

        // Trigger route guard to re-execute, which will automatically redirect
        // based on the new onboarding state via the unified state machine
        await router.invalidate();

        // Note: No manual redirect needed here. The route guard's state machine
        // will automatically determine the correct destination (e.g., /onboarding/waitlist/pending)
        // based on the new waitlist_submitted state.
      } catch (err) {
        logError("onboarding.waitlist.failed", {
          error: err instanceof Error ? err.message : "unknown-error",
        });
        setError(extractApiErrorMessage(err, t("pages:onboarding.waitlist.form.joinError")));
      } finally {
        setIsLoading(false);
      }
    },
    [router, t],
  );

  const handleBackToActivation = useCallback(() => {
    navigate({ to: "/onboarding/activation" });
  }, [navigate]);

  return (
    <OnboardingLayout showLogo={false}>
      <WaitlistFormCard
        onSubmit={handleWaitlistSubmit}
        error={error}
        isLoading={isLoading}
        onBack={handleBackToActivation}
      />
    </OnboardingLayout>
  );
}
